import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface PCCountdownTimerProps {
  expirationTime: number;
  isMobile?: boolean;
}

export const PCCountdownTimer = ({ expirationTime, isMobile = false }: PCCountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize timer
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    const now = Date.now();
    const timeLeft = Math.max(0, expirationTime - now);
    const timeLeftSeconds = Math.ceil(timeLeft / 1000);
    
    if (timeLeftSeconds > 0) {
      setTimeLeft(timeLeftSeconds);
      setIsVisible(true);
      setIsExpired(false);
      
      // Start countdown
      timerRef.current = setInterval(() => {
        const currentTime = Date.now();
        const newTime = Math.max(0, Math.ceil((expirationTime - currentTime) / 1000));
        
        if (newTime <= 0) {
          setIsExpired(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setTimeLeft(0);
        } else {
          setTimeLeft(newTime);
        }
      }, 1000);
    } else {
      setIsVisible(false);
      setIsExpired(true);
    }
  }, [expirationTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  if (!isVisible && !isExpired) return null;

  const formatTime = (timeLeft: number) => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getBackgroundClass = () => {
    if (isExpired) return 'bg-red-800';
    if (timeLeft <= 10) return 'bg-red-600';
    if (timeLeft <= 30) return 'bg-orange-600';
    return 'bg-blue-600';
  };

  return (
    <div className={clsx(
      'fixed z-50',
      isMobile 
        ? 'bottom-4 right-4' // Mobile: bottom right corner
        : 'left-1/2 top-0 mt-20 -translate-x-1/2' // Desktop: center top
    )}>
      <div
        className={clsx(
          'flex h-12 items-center gap-3 rounded-lg border-2 border-white px-4 py-1 font-[Syne] font-semibold text-white transition-colors duration-200',
          getBackgroundClass()
        )}
      >
        <span className='text-xl'>⏱</span>
        <span className='text-xl font-mono'>
          {isExpired ? 'Lobby Closing...' : formatTime(timeLeft)}
        </span>
      </div>
    </div>
  );
}; 